import Modal from "react-bootstrap/Modal";
import { Table, Form, Row, Col, Button } from "react-bootstrap";
import useSensor from "../../../Hooks/useSensor";
import { useEffect, useState, useMemo } from "react";
import moment from "moment";
import Loading from "../../../components/LoadingPage/Loading";
import DatePicker from "react-date-picker";
import { generateTemperatureLogPDF, openTemperatureLogPDF } from "./temperatureLogPdf";
import { toast } from "react-toastify";

interface TemperatureLog {
  timeStamp: string;
  temp: number;
  alert: boolean;
  minTemp?: number;
  maxTemp?: number;
}

interface SensorLogsResponse {
  temperatureLogs: TemperatureLog[];
  higherTempLimit: number;
  lowerTempLimit: number;
}

const DEFAULT_MONTH = moment().startOf("month").toDate();
const DEFAULT_FREQUENCY = "Daily";

const FREQUENCY_OPTIONS = [
  { value: "Daily", label: "Daily" },
  // { value: "Hourly", label: "Hourly" },
  // { value: 3, label: "Weekly" },
] as const;

export const TemperatureLogReportModel = ({
  showReportModal,
  onCloseReportModal,
  selectedSensor,
}: {
  showReportModal: boolean;
  onCloseReportModal: () => void;
  selectedSensor: {
    id: string;
    serialNumber: string;
    sensorName?: string;
    inCentigrade: boolean;
    lowerTempLimit?: number;
    higherTempLimit?: number;
    refInfo: any;
  } | null;
}) => {
  const [reportData, setReportData] = useState<any | null>(null);
  const [selectedFrequency, setSelectedFrequency] = useState<string>(DEFAULT_FREQUENCY);
  const [selectedMonth, setSelectedMonth] = useState<Date>(DEFAULT_MONTH);

  const {
    // getSensorsLogAPI,
    // getSensorsLogLoading,
    getRefrigeratorLogsReportAPI,
    getRefrigeratorLogsReportLoading,
    exportSensorLogsAPI,
    exportSensorLogsLoading,
  } = useSensor();

  const refInfo = useMemo(() => selectedSensor?.refInfo || {}, [selectedSensor]);

  const fetchReportData = async (month: Date = selectedMonth, frequency: string = selectedFrequency) => {
    if (!refInfo?.sensorRefrigerator?.refrigeratorId) return;

    const effectiveStartDate = moment(month).startOf("month").format("YYYY-MM-DD");
    const effectiveEndDate = moment(month).isSame(moment(), "month")
      ? moment().format("YYYY-MM-DD")
      : moment(month).endOf("month").format("YYYY-MM-DD");

    try {
      const response: SensorLogsResponse = await getRefrigeratorLogsReportAPI({
        refrigeratorId: refInfo?.sensorRefrigerator?.refrigeratorId || "",
        fromDate: moment(effectiveStartDate).format("YYYY-MM-DD"),
        toDate: moment(effectiveEndDate).format("YYYY-MM-DD"),
        clientCode: localStorage.getItem("profileClientId") || "",
        dataFrequency: frequency,
      });
      setReportData(response);
    } catch (error) {
      console.error("Error fetching sensor logs:", error);
      setReportData(null);
    }
  };

  useEffect(() => {
    if (showReportModal && refInfo) {
      fetchReportData();
    }
  }, [showReportModal, refInfo]);

  const handleFrequencyChange = (e: any) => {
    setSelectedFrequency(e.target.value);
  };

  const getFrequencyLabel = (value: string) => {
    return FREQUENCY_OPTIONS.find((opt) => opt.value === value)?.label || "";
  };

  const handleViewReport = () => {
    fetchReportData();
  };

  const handleClearAll = () => {
    const isAlreadyDefault = moment(selectedMonth).isSame(DEFAULT_MONTH, "month") && selectedFrequency === DEFAULT_FREQUENCY;
    if (isAlreadyDefault) return;

    setSelectedFrequency(DEFAULT_FREQUENCY);
    setSelectedMonth(DEFAULT_MONTH);
    fetchReportData(DEFAULT_MONTH, DEFAULT_FREQUENCY);
  };

  const exportReport = async () => {
    if (!selectedSensor?.id) return;

    try {
      const response = await exportSensorLogsAPI({
        id: selectedSensor.id,
        startDate: moment(startDate).format("MM-DD-YYYY"),
        endDate: moment(endDate).format("MM-DD-YYYY"),
        frequency: selectedFrequency,
      });
      const url = URL.createObjectURL(response);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${localStorage.getItem("profileClientId")?.toUpperCase() ?? ""}_Temperature_Log_Report_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error exporting report:", error);
    }
  };

  const [pdfLoading, setPdfLoading] = useState(false);

  const temperatureUnit = useMemo(
    () => (selectedSensor?.inCentigrade ? "°C" : "°F"),
    [selectedSensor?.inCentigrade]
  );

  const handlePrintPDF = async () => {
    if (!reportData?.length) {
      toast.warning("No report data available to print.");
      return;
    }
    setPdfLoading(true);
    try {
      const doc = await generateTemperatureLogPDF({
        reportData,
        selectedMonth,
        temperatureUnit,
        clientCode: localStorage.getItem("profileClientId") || "",
        refrigeratorName: refInfo?.refrigeratorName || "",
      });
      openTemperatureLogPDF(doc);
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF report.");
    } finally {
      setPdfLoading(false);
    }
  };

  const startDate = useMemo(
    () => moment(selectedMonth).startOf("month").format("YYYY-MM-DD"),
    [selectedMonth]
  );

  const endDate = useMemo(() => {
    const isCurrentMonth = moment(selectedMonth).isSame(moment(), "month");
    return isCurrentMonth
      ? moment().format("YYYY-MM-DD")
      : moment(selectedMonth).endOf("month").format("YYYY-MM-DD");
  }, [selectedMonth]);

  const recordCount = reportData?.length ?? 0;

  return (
    <Modal
      size="xl"
      id="trans-report-modal"
      show={showReportModal}
      onHide={onCloseReportModal}
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h4>View Temperature Log Report</h4>
          {/* <div className="trans-report-action-wrapper">
            <div className="trans-report-action-box export-report-opt-box">
              <ReactSVG className="svg-box" wrapper="span" src={exporticon} />
              <NavDropdown title="Export Report">
                <NavDropdown.Item onClick={() => exportReport()}>
                  <img src={excelicon} alt="" /> Excel
                </NavDropdown.Item>
              </NavDropdown>
              <ReactSVG className="svg-box" wrapper="span" src={arrowdown} />
            </div>
          </div> */}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {getRefrigeratorLogsReportLoading || exportSensorLogsLoading || pdfLoading ? <Loading /> : null}
        <div className="trans-report-content-wrapper">
          <div className="trans-report-content-header">
            <div className="header-content">
              <h5>Temperature Log Report</h5>
              <p>
                {moment(startDate).format("MM/DD/YYYY")} - {moment(endDate).format("MM/DD/YYYY")} ({getFrequencyLabel(selectedFrequency)})
              </p>
            </div>
            <div className="header-logo-wrapper">
              <div className="header-logo-box srx-logo-box">
                <div className="trans-report-detail-box text-center">
                  <label>Refrigerator Name</label>
                  <p>{refInfo?.refrigeratorName || "N/A"}</p>
                </div>
                <div className="trans-report-detail-box text-center">
                  <label>Temperature Unit</label>
                  <p>{temperatureUnit || "N/A"}</p>
                </div>
                <div className="trans-report-detail-box text-center">
                  <label>Lower Limit</label>
                  <p>
                    {reportData?.lowerTempLimit ?? selectedSensor?.lowerTempLimit ?? "N/A"}{" "}
                    {reportData?.lowerTempLimit || selectedSensor?.lowerTempLimit ? temperatureUnit : ""}
                  </p>
                </div>
                <div className="trans-report-detail-box text-center">
                  <label>Upper Limit</label>
                  <p>
                    {reportData?.higherTempLimit ?? selectedSensor?.higherTempLimit ?? "N/A"}{" "}
                    {reportData?.higherTempLimit || selectedSensor?.higherTempLimit ? temperatureUnit : ""}
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="trans-report-detail-box-wrapper">
            <div className="trans-report-filters">
              <Row className="align-items-end g-3">
                <Col xs="auto">
                  <Form.Group>
                    <Form.Label>Select Month</Form.Label>
                    <DatePicker
                      locale="en-US"
                      format="MM/yyyy"
                      maxDetail="year"
                      className="form-control"
                      value={selectedMonth}
                      maxDate={moment().endOf("month").toDate()}
                      onChange={(date: any) => {
                        if (date) setSelectedMonth(new Date(date));
                      }}
                      clearIcon={null}
                      calendarIcon={<i className="bi bi-calendar"></i>}
                    />
                  </Form.Group>
                </Col>
                {/* <Col xs="auto">
                  <Form.Group>
                    <Form.Label>Frequency</Form.Label>
                    <Form.Control
                      as="select"
                      value={selectedFrequency}
                      onChange={handleFrequencyChange}
                      style={{ minWidth: "120px" }}
                    >
                      {FREQUENCY_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Form.Control>
                  </Form.Group>
                </Col> */}
                <Col xs="auto">
                  <Button variant="primary" onClick={handleViewReport}>
                    View Report
                  </Button>
                  <Button variant="link" onClick={handleClearAll} className="ms-2 clear-all-btn">
                    Clear All
                  </Button>
                </Col>
                <Col xs="auto" className="ms-auto">
                  <Button
                    variant="outline-secondary"
                    onClick={handlePrintPDF}
                    disabled={!reportData?.length}
                  >
                    <i className="bi bi-printer me-1"></i>
                    Print
                  </Button>
                </Col>
              </Row>
            </div>

            <div className="trans-report-table-wrapper" style={{ overflow: "auto", maxHeight: "350px" }}>
              <Table bordered={false} hover={true}>
                <thead>
                  <tr>
                    <th>Sensor Id</th>
                    <th>Log Date</th>
                    <th className="text-center">Min</th>
                    <th className="text-center">Max</th>
                    <th className="text-center">Average</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData?.length ? (
                    reportData.map((log: any, index: any) => (
                      <tr key={index}>
                        <td>{log?.sensorId}</td>
                        <td>{log.sensorLogDateTime ? moment(log.sensorLogDateTime).format("MM/DD/YYYY") : "N/A"}</td>
                        <td className="text-center">{log.min != null ? Number(log.min).toFixed(1) : ""}</td>
                        <td className="text-center">{log.max != null ? Number(log.max).toFixed(1) : ""}</td>
                        <td className="text-center">{log.avg != null ? Number(log.avg).toFixed(1) : ""}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={6} className="text-center">No temperature logs found</td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>

            <div className="record-count">{recordCount} record(s)</div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
};
